import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { App, Button, Flex, Table } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { StringParam } from "use-query-params";
import _ from "lodash";

import {
  FIELD_KEYS,
  FIELD_LABELS,
  FIELD_ORDERS,
  HISTORICAL_ONLY_FIELDS,
  RESOLVE_ACTION,
  TABLE_FIELDS,
} from "constants/ErrorFields";
import { useProduct } from "hooks/useProduct";
import { encodeQueryParamsWithProducts } from "functions/queryParamFuncs";
import CollapsibleText from "components/Common/CollapsibleText";
import DateTime from "components/Common/DateTime";
import ErrorDetailsDrawer from "components/ErrorList/ErrorDetailsDrawer";
import useFetch from "hooks/useFetch";
import StatusTag from "components/Common/StatusTag";

const columnAdditionalConfig = {
  [FIELD_KEYS.NO]: {
    align: "center",
  },
  [FIELD_KEYS.PRODUCT]: {
    width: "15rem",
  },
  [FIELD_KEYS.UNIT_X]: {
    align: "center",
  },
  [FIELD_KEYS.UNIT_START_DATE_TIME]: {
    align: "center",
    width: "10rem",
  },
  [FIELD_KEYS.ERROR_DATE_TIME]: {
    align: "center",
    width: "10rem",
  },
  [FIELD_KEYS.ERROR_DETAILS]: {
    width: "15rem",
  },
  [FIELD_KEYS.ACTION]: {
    align: "center",
  },
};

const ErrorTable = ({ isHistoricalView, dataSource, loading, onRefresh }) => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { loading: loadingResolveError, doFetch: postResolveError } = useFetch(
    "/unitErrors/resolveUnitError",
    "POST",
    []
  );
  const { selectedProductIds } = useProduct();
  const [showErrorDetailsDrawer, setShowErrorDetailsDrawer] = useState(false);
  const [viewErrorIndex, setViewErrorIndex] = useState();

  const fields = isHistoricalView
    ? _.difference(TABLE_FIELDS, [FIELD_KEYS.ERROR_DETAILS])
    : _.difference(TABLE_FIELDS, HISTORICAL_ONLY_FIELDS);
  const columns = _.intersection(FIELD_ORDERS, fields).map((field) => ({
    title: FIELD_LABELS[field],
    dataIndex: field,
    render: (value, index, row) => renderField(field, value, index, row),
    ...(columnAdditionalConfig[field] ?? {}),
  }));

  const closeErrorDetailsDrawer = () => {
    setShowErrorDetailsDrawer(false);
    setViewErrorIndex();
  };

  const navigateToItemDetails = (itemUuid) => {
    return navigate({
      pathname: "/items/details",
      search: `?${encodeQueryParamsWithProducts(
        selectedProductIds,
        { itemUuid: StringParam },
        { item: itemUuid }
      )}`,
    });
  };

  const openErrorDetailsDrawer = (index) => {
    setViewErrorIndex(index);
    setShowErrorDetailsDrawer(true);
  };

  const renderField = (field, val, row, index) => {
    switch (field) {
      case FIELD_KEYS.NO:
        return index + 1;
      case FIELD_KEYS.ITEM_REF_NO:
        return (
          <Button
            type="link"
            onClick={() => navigateToItemDetails(row.item_uuid)}
            style={{ padding: 0 }}
            className="user-selectable"
          >
            {val}
          </Button>
        );
      case FIELD_KEYS.UNIT_STATUS:
        return <StatusTag status={val} />;
      case FIELD_KEYS.UNIT_START_DATE_TIME:
      case FIELD_KEYS.ERROR_DATE_TIME:
        return <DateTime utcValue={val} />;
      case FIELD_KEYS.PRODUCT:
      case FIELD_KEYS.ERROR_DETAILS:
        return (
          <CollapsibleText
            width={columnAdditionalConfig[field]?.width}
            content={val}
          />
        );
      case FIELD_KEYS.ACTION:
        return (
          <Flex justify="center">
            {!isHistoricalView && (
              <Button
                onClick={() => resolveError(row.error_id, RESOLVE_ACTION.RETRY)}
                loading={loadingResolveError}
                icon={<ReloadOutlined />}
              >
                Retry
              </Button>
            )}

            <Button type="link" onClick={() => openErrorDetailsDrawer(index)}>
              View
            </Button>
          </Flex>
        );
      default:
        return val ?? "-";
    }
  };

  const resolveError = (errorId, resolveAction) => {
    postResolveError({
      error_id: errorId,
      resolve_action: resolveAction,
    }).then((response) => {
      if (!!response?.success) {
        message.success(response?.data?.data);
        closeErrorDetailsDrawer();
        onRefresh();
      }
    });
  };

  return (
    <>
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey={(record) => record.error_id}
        loading={loading}
        pagination={false}
        size="middle"
      />
      <ErrorDetailsDrawer
        open={showErrorDetailsDrawer}
        isHistoricalView={isHistoricalView}
        data={dataSource[viewErrorIndex] ?? {}}
        loadingResolveError={loadingResolveError}
        onClose={closeErrorDetailsDrawer}
        onResolveError={resolveError}
      />
    </>
  );
};

export default ErrorTable;
